import React, { useEffect, useMemo, type JSX } from 'react';

import { PortaledLayer } from './PortaledLayer';
import {
  buildRaceValidationReportCsv,
  RaceValidationReportTable,
} from './RaceValidationReportTable';
import type { RaceValidationReportRow } from './types/ValidationReportTypes';

type Props = {
  open: boolean;
  onClose: () => void;
  eventId: string;
  raceId: string;
  rows: RaceValidationReportRow[];
  failedCount: number;
};

export default function ValidationReportDialog(props: Props): JSX.Element | null {
  const { open, onClose, eventId, raceId, rows, failedCount } = props;

  const csvText = useMemo(
    () =>
      buildRaceValidationReportCsv({
        eventId: eventId || '',
        raceId: raceId || '',
        rows,
      }),
    [eventId, raceId, rows],
  );

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  async function copyCsv(): Promise<void> {
    try {
      if (!navigator?.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(csvText);
      window.dispatchEvent(new CustomEvent('toast-info', { detail: 'Validation report CSV copied' }));
    } catch {
      window.dispatchEvent(new CustomEvent('toast-error', { detail: 'Unable to copy CSV' }));
    }
  }

  function downloadCsv(): void {
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const eventPart = String(eventId || 'validation')
      .trim()
      .replace(/[^a-zA-Z0-9_-]+/g, '_');
    const racePart = String(raceId || '')
      .trim()
      .replace(/[^a-zA-Z0-9_-]+/g, '_');
    const link = document.createElement('a');
    link.href = url;
    link.download = racePart
      ? `validation-report-${eventPart}-${racePart}.csv`
      : `validation-report-${eventPart}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  if (!open) return null;

  const scopeLine = [eventId, raceId].filter((s) => String(s).trim()).join(' · ');

  return (
    <PortaledLayer>
      <div
        className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40 p-3 dark:bg-black/70 sm:p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="devices-validation-report-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="flex max-h-[min(92vh,900px)] w-full max-w-[min(1200px,100vw-1.5rem)] flex-col overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
            <div className="min-w-0">
              <h2 id="devices-validation-report-title" className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                Validation report
              </h2>
              {scopeLine ? (
                <p className="mt-0.5 break-words text-xs text-gray-500 dark:text-slate-400">{scopeLine}</p>
              ) : null}
              <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
                {rows.length} device{rows.length === 1 ? '' : 's'}, {failedCount} failed
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void copyCsv()}
                disabled={rows.length === 0}
                className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Copy CSV
              </button>
              <button
                type="button"
                onClick={downloadCsv}
                disabled={rows.length === 0}
                className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Download CSV
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
              >
                Close
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto bg-white px-4 py-3 dark:bg-slate-900">
            {rows.length === 0 ? (
              <p className="text-sm text-gray-600 dark:text-slate-400">No validation rows to display.</p>
            ) : (
              <RaceValidationReportTable rows={rows} eventId={eventId} />
            )}
          </div>
        </div>
      </div>
    </PortaledLayer>
  );
}
